import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { X, Upload, Image } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BookingForm({ room, onClose, onSuccess, existingBookings, isPublic = false, editingBooking = null }) {
  const resolveImageUrl = (imageUrl) => {
    if (!imageUrl) {
      return null;
    }
    return imageUrl.startsWith('http') ? imageUrl : `${BACKEND_URL}${imageUrl}`;
  };

  const [formData, setFormData] = useState({
    date: '',
    startTime: '',
    endTime: '',
    meetingName: '',
    responsible: '',
    recurrence: 'none',
    recurrenceEndDate: '',
    welcomeImage: null,
    visitorName: '',
    displayMessage: ''
  });
  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    if (editingBooking) {
      const startDate = new Date(editingBooking.start_time);
      const endDate = new Date(editingBooking.end_time);
      
      setFormData({
        date: startDate.toISOString().split('T')[0],
        startTime: startDate.toTimeString().slice(0, 5),
        endTime: endDate.toTimeString().slice(0, 5),
        meetingName: editingBooking.meeting_name || '',
        responsible: editingBooking.responsible || '',
        recurrence: 'none',
        recurrenceEndDate: '',
        welcomeImage: null,
        visitorName: editingBooking.visitor_name || '',
        displayMessage: editingBooking.display_message || ''
      });
      
      // Se já tem imagem, mostrar preview
      if (editingBooking.welcome_image) {
        setImagePreview(resolveImageUrl(editingBooking.welcome_image));
      }
    }
  }, [editingBooking]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validar tipo
      if (!file.type.startsWith('image/')) {
        toast.error('Por favor, selecione um arquivo de imagem');
        return;
      }
      // Validar tamanho (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('A imagem deve ter no máximo 5MB');
        return;
      }
      
      setFormData({ ...formData, welcomeImage: file });
      
      // Criar preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setFormData({ ...formData, welcomeImage: null });
    setImagePreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const startDateTime = `${formData.date}T${formData.startTime}:00-03:00`;
      const endDateTime = `${formData.date}T${formData.endTime}:00-03:00`;

      // Se tem imagem, fazer upload primeiro
      let welcomeImageUrl = editingBooking?.welcome_image || null;
      
      if (formData.welcomeImage) {
        const imageFormData = new FormData();
        imageFormData.append('file', formData.welcomeImage);
        
        const uploadEndpoint = isPublic ? `${API}/public/upload-image` : `${API}/upload-image`;
        const uploadResponse = await axios.post(uploadEndpoint, imageFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        welcomeImageUrl = uploadResponse.data.url;
      }

      const endpoint = isPublic 
        ? (editingBooking ? `${API}/public/bookings/${editingBooking.id}` : `${API}/public/bookings`)
        : (editingBooking ? `${API}/bookings/${editingBooking.id}` : `${API}/bookings`);
      
      const payload = {
        room_id: room.id,
        start_time: startDateTime,
        end_time: endDateTime,
        meeting_name: formData.meetingName,
        responsible: formData.responsible,
        visitor_name: formData.visitorName || null,
        welcome_image: welcomeImageUrl,
        display_message: formData.displayMessage || null
      };

      if (!editingBooking && formData.recurrence !== 'none') {
        payload.recurrence = formData.recurrence;
        payload.recurrence_end_date = `${formData.recurrenceEndDate}T23:59:59-03:00`;
      }

      if (editingBooking) {
        await axios.put(endpoint, payload);
        toast.success('Reunião atualizada com sucesso!');
      } else {
        const response = await axios.post(endpoint, payload);
        const count = response.data.count || 1;
        toast.success(`${count} reunião(ões) criada(s) com sucesso!`);
      }

      setLoading(false);
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar reserva');
      setLoading(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-8 relative max-h-[90vh] overflow-y-auto">
        <button
          data-testid="close-booking-form-button"
          onClick={onClose}
          className="absolute top-6 right-6 text-muted-foreground hover:text-foreground transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="mb-8">
          <h2 className="text-3xl font-manrope font-bold tracking-tight mb-2">
            {editingBooking ? 'Editar Reunião' : `Reservar ${room.name}`}
          </h2>
          <p className="text-muted-foreground font-inter">
            Capacidade: <span className="font-mono font-medium">{room.capacity} pessoas</span>
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="meetingName" className="text-sm font-medium">
              Nome da Reunião *
            </Label>
            <Input
              id="meetingName"
              data-testid="booking-meeting-name-input"
              type="text"
              value={formData.meetingName}
              onChange={(e) => setFormData({ ...formData, meetingName: e.target.value })}
              placeholder="Ex: Reunião de Planejamento Trimestral"
              required
              className="h-12 bg-white/50 backdrop-blur-sm border-border/50 focus:border-primary"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="responsible" className="text-sm font-medium">
              Responsável *
            </Label>
            <Input
              id="responsible"
              data-testid="booking-responsible-input"
              type="text"
              value={formData.responsible}
              onChange={(e) => setFormData({ ...formData, responsible: e.target.value })}
              placeholder="Nome do responsável pela reunião"
              required
              className="h-12 bg-white/50 backdrop-blur-sm border-border/50 focus:border-primary"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="visitorName" className="text-sm font-medium">
              Nome do Visitante/Cliente (opcional)
            </Label>
            <Input
              id="visitorName"
              data-testid="booking-visitor-name-input"
              type="text"
              value={formData.visitorName}
              onChange={(e) => setFormData({ ...formData, visitorName: e.target.value })}
              placeholder="Nome que aparecerá na tela de boas-vindas"
              className="h-12 bg-white/50 backdrop-blur-sm border-border/50 focus:border-primary"
            />
            <p className="text-xs text-muted-foreground">
              Se preenchido, exibirá uma mensagem de boas-vindas no monitor da sala
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="displayMessage" className="text-sm font-medium">
              Mensagem do Display (opcional)
            </Label>
            <Textarea
              id="displayMessage"
              data-testid="booking-display-message-input"
              value={formData.displayMessage}
              onChange={(e) => setFormData({ ...formData, displayMessage: e.target.value })}
              placeholder="Ex: Bem-vindo ao nosso escritório. Sua reunião começará em instantes."
              rows={4}
              className="bg-white/50 backdrop-blur-sm border-border/50 focus:border-primary"
            />
            <p className="text-xs text-muted-foreground">
              Essa mensagem será exibida no monitor da sala durante a reunião
            </p>
          </div>

          {/* Upload de Imagem de Boas-Vindas */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">
              Imagem de Boas-Vindas (opcional)
            </Label>
            
            {imagePreview ? (
              <div className="relative">
                <div className="relative w-full aspect-[9/16] max-h-48 bg-gray-100 rounded-lg overflow-hidden">
                  <img 
                    src={imagePreview} 
                    alt="Preview" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <button
                  type="button"
                  onClick={removeImage}
                  className="absolute -top-2 -right-2 bg-destructive text-white rounded-full p-1 hover:bg-destructive/90 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-border/50 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <Upload className="w-8 h-8 mb-2 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    Clique para enviar imagem
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Proporção ideal: 9x16 (vertical)
                  </p>
                </div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                  data-testid="booking-welcome-image-input"
                />
              </label>
            )}
            <p className="text-xs text-muted-foreground">
              A imagem será exibida no monitor da sala para dar boas-vindas ao visitante
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="date" className="text-sm font-medium">
              Data *
            </Label>
            <Input
              id="date"
              data-testid="booking-date-input"
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              min={new Date().toISOString().split('T')[0]}
              required
              className="h-12 bg-white/50 backdrop-blur-sm border-border/50 focus:border-primary"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="startTime" className="text-sm font-medium">
                Hora Início *
              </Label>
              <Input
                id="startTime"
                data-testid="booking-start-time-input"
                type="time"
                value={formData.startTime}
                onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                required
                className="h-12 bg-white/50 backdrop-blur-sm border-border/50 focus:border-primary font-mono"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="endTime" className="text-sm font-medium">
                Hora Fim *
              </Label>
              <Input
                id="endTime"
                data-testid="booking-end-time-input"
                type="time"
                value={formData.endTime}
                onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                required
                className="h-12 bg-white/50 backdrop-blur-sm border-border/50 focus:border-primary font-mono"
              />
            </div>
          </div>

          {!editingBooking && (
            <>
              <div className="space-y-2">
                <Label htmlFor="recurrence" className="text-sm font-medium">
                  Recorrência
                </Label>
                <select
                  id="recurrence"
                  data-testid="booking-recurrence-select"
                  value={formData.recurrence}
                  onChange={(e) => setFormData({ ...formData, recurrence: e.target.value })}
                  className="w-full h-12 bg-white/50 backdrop-blur-sm border border-border/50 rounded-md px-3 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  <option value="none">Não repetir</option>
                  <option value="weekly">Semanal</option>
                  <option value="biweekly">Quinzenal</option>
                  <option value="monthly">Mensal</option>
                </select>
              </div>

              {formData.recurrence !== 'none' && (
                <div className="space-y-2">
                  <Label htmlFor="recurrenceEndDate" className="text-sm font-medium">
                    Repetir até *
                  </Label>
                  <Input
                    id="recurrenceEndDate"
                    data-testid="booking-recurrence-end-date-input"
                    type="date"
                    value={formData.recurrenceEndDate}
                    onChange={(e) => setFormData({ ...formData, recurrenceEndDate: e.target.value })}
                    min={formData.date}
                    required={formData.recurrence !== 'none'}
                    className="h-12 bg-white/50 backdrop-blur-sm border-border/50 focus:border-primary"
                  />
                  <p className="text-xs text-muted-foreground">
                    A reunião se repetirá {formData.recurrence === 'weekly' ? 'toda semana' : formData.recurrence === 'biweekly' ? 'a cada 2 semanas' : 'todo mês'} até esta data
                  </p>
                </div>
              )}
            </>
          )}

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              className="flex-1 h-12 rounded-full"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              data-testid="confirm-booking-button"
              disabled={loading}
              className="flex-1 h-12 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-semibold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
            >
              {loading ? (editingBooking ? 'Salvando...' : 'Reservando...') : (editingBooking ? 'Salvar Alterações' : 'Confirmar Reserva')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
